import { Alert, Card, Collapse, Space, Typography } from 'antd';
import { E2EAttempt, E2EResult, E2ESuite, E2ETestAttempt } from '../../../domain/models/E2ERun';
import E2EMediaPlayer from './E2EMediaPlayer';
import E2EStatus from './E2EStatus';

type Context = { runKey: string; attempt: E2EAttempt };
function TestAttemptCard({
  runKey,
  attempt,
  testAttempt,
}: Context & { testAttempt: E2ETestAttempt }) {
  return (
    <Card
      size="small"
      title={`Tentative du parcours ${testAttempt.index + 1}`}
      style={{ marginBottom: 12 }}
    >
      <Space wrap>
        <E2EStatus status={testAttempt.status} />
        <span>
          {testAttempt.durationMs} ms · nettoyage {testAttempt.cleanup}
        </span>
      </Space>
      {testAttempt.error && (
        <Alert
          type="error"
          style={{ marginTop: 12 }}
          message={[testAttempt.error.code, testAttempt.error.phase].filter(Boolean).join(' · ')}
        />
      )}
      {testAttempt.steps.map((step, index) => (
        <Typography.Paragraph
          key={`${step.type}-${index}`}
          style={{ marginTop: 8, marginBottom: 8 }}
        >
          {step.title ?? step.type} · <E2EStatus status={step.status} />
          {step.durationMs !== undefined && `${step.durationMs} ms`}
        </Typography.Paragraph>
      ))}
      {testAttempt.mediaIds.map((id) => {
        const media = attempt.media.find((m) => m.id === id);
        return media ? (
          <E2EMediaPlayer
            key={`${runKey}-${attempt.runAttempt}-${id}`}
            runKey={runKey}
            runAttempt={attempt.runAttempt}
            media={media}
          />
        ) : null;
      })}
    </Card>
  );
}
function ResultAttempts({ result, ...context }: Context & { result: E2EResult }) {
  if (!result.attempts.length)
    return <Typography.Text type="secondary">Ce parcours n’a pas été exécuté.</Typography.Text>;
  return (
    <>
      {result.attempts.map((testAttempt) => (
        <TestAttemptCard key={testAttempt.id} {...context} testAttempt={testAttempt} />
      ))}
    </>
  );
}
function SuiteResults({ suite, ...context }: Context & { suite: E2ESuite }) {
  if (!suite.results.length)
    return (
      <Typography.Text type="secondary">
        Aucun rapport de parcours disponible pour cette suite.
      </Typography.Text>
    );
  return (
    <Collapse
      items={suite.results.map((result) => ({
        key: result.id,
        label: (
          <Space>
            {result.titlePath.join(' / ')}
            <E2EStatus status={result.status} />
            {!result.selected && 'Non sélectionné'}
          </Space>
        ),
        children: <ResultAttempts result={result} {...context} />,
      }))}
    />
  );
}
function suiteLabel(suite: E2ESuite) {
  return (
    <Space>
      {suite.id}
      {suite.id === 'negative-control' && (
        <Typography.Text type="secondary">Contrôle technique</Typography.Text>
      )}
      <E2EStatus status={suite.reportAvailability} />
      <span>
        Gate : <E2EStatus status={suite.validation} />
      </span>
    </Space>
  );
}
export default function E2ESuiteResults(context: Context) {
  return (
    <Collapse
      style={{ marginTop: 20 }}
      items={context.attempt.suites.map((suite) => ({
        key: suite.id,
        label: suiteLabel(suite),
        children: <SuiteResults suite={suite} {...context} />,
      }))}
    />
  );
}
